import { useId, useRef, useState } from "react";
import type { Dispatch, ReactNode, SetStateAction } from "react";
import { Icon } from "@common/index";
import { BottomSheet } from "@customer/Search/BottomSheet";
import { useDialogFocusTrap } from "@/hooks/useDialogFocusTrap";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type { InvoiceKindType, InvoiceStatusType } from "@models/invoice";
import { toggled } from "@utils/toggled";
import {
    BILLING_HISTORY_KIND_OPTIONS,
    BILLING_HISTORY_STATUS_OPTIONS,
} from "../_helpers/billingHistoryParams";

interface InvoiceFiltersDrawerProps {
    // The committed (URL) filters the drawer opens with.
    kinds: InvoiceKindType[];
    statuses: InvoiceStatusType[];
    onApply: (kinds: InvoiceKindType[], statuses: InvoiceStatusType[]) => void;
    // Keep it stable: BottomSheet keys its keydown listener on it.
    onClose: () => void;
}

interface FilterDraft {
    kinds: InvoiceKindType[];
    statuses: InvoiceStatusType[];
}

// Tailwind's `md`, where the drawer turns from a bottom sheet into a panel.
const DESKTOP_QUERY = "(min-width: 768px)";

// The Billing History "Filters" drawer (#215). Mounted only while
// isInvoiceFiltersOpenAtom is true, and keyed by the page on the URL, so a
// URL change under it (a browser Back) remounts it with a fresh draft rather
// than letting a stale one write the old filters back on Apply.
//
// Every change is buffered in the draft and committed only on Apply; Close,
// Escape and the backdrop discard. Mobile composes the shared BottomSheet;
// from md up it is a right-hand panel on the shared useDialogFocusTrap
// chassis (BottomSheet is mobile-only). Exactly one of the two is mounted,
// so there is one focus trap and one scroll lock.
export const InvoiceFiltersDrawer = ({
    kinds,
    statuses,
    onApply,
    onClose,
}: InvoiceFiltersDrawerProps) => {
    const [draft, setDraft] = useState<FilterDraft>({ kinds, statuses });
    const isDesktop = useMediaQuery(DESKTOP_QUERY);

    const apply = () => onApply(draft.kinds, draft.statuses);
    const fields = <InvoiceFilterFields draft={draft} setDraft={setDraft} />;

    return isDesktop ? (
        <DesktopPanel onApply={apply} onClose={onClose}>
            {fields}
        </DesktopPanel>
    ) : (
        <BottomSheet
            title="Filters"
            testId="invoice-filter-sheet"
            onApply={apply}
            onClose={onClose}
        >
            {fields}
        </BottomSheet>
    );
};

interface DesktopPanelProps {
    onApply: () => void;
    onClose: () => void;
    children: ReactNode;
}

// The md+ half: BottomSheet's header (Close / title / Apply) on a right-hand
// panel.
const DesktopPanel = ({ onApply, onClose, children }: DesktopPanelProps) => {
    const panelRef = useRef<HTMLDivElement>(null);
    const titleId = useId();
    const { backdropProps, panelProps } = useDialogFocusTrap({
        panelRef,
        isProcessing: false,
        onRequestClose: onClose,
    });

    return (
        <div
            className="fixed inset-0 z-50 flex justify-end bg-black/40"
            {...backdropProps}
        >
            <div
                ref={panelRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={titleId}
                data-testid="invoice-filter-panel"
                {...panelProps}
                className="flex w-96 flex-col bg-base-100 text-neutral shadow-xl focus:outline-none"
            >
                <div className="flex items-center justify-between gap-2 border-b border-base-200 px-3 py-2">
                    <button
                        type="button"
                        aria-label="Close"
                        onClick={onClose}
                        className="btn btn-ghost btn-sm btn-square text-neutral"
                    >
                        <Icon name="close" className="text-2xl" />
                    </button>
                    <h2 id={titleId} className="font-bold text-neutral">
                        Filters
                    </h2>
                    <button
                        type="button"
                        onClick={onApply}
                        className="btn btn-secondary btn-sm font-bold"
                    >
                        Apply
                    </button>
                </div>
                <div className="flex-1 overflow-y-auto">{children}</div>
            </div>
        </div>
    );
};

interface InvoiceFilterFieldsProps {
    draft: FilterDraft;
    setDraft: Dispatch<SetStateAction<FilterDraft>>;
}

// The two checkbox groups, shared by both halves. They edit the draft only.
const InvoiceFilterFields = ({ draft, setDraft }: InvoiceFilterFieldsProps) => (
    <div className="flex flex-col gap-6 p-4">
        <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 font-bold">Type</legend>
            {BILLING_HISTORY_KIND_OPTIONS.map((option) => (
                <label
                    key={option.slug}
                    className="flex cursor-pointer items-center gap-3"
                >
                    <input
                        type="checkbox"
                        className="checkbox checkbox-primary"
                        checked={draft.kinds.includes(option.kind)}
                        onChange={() =>
                            setDraft((prev) => ({
                                ...prev,
                                kinds: toggled(prev.kinds, option.kind),
                            }))
                        }
                    />
                    {option.label}
                </label>
            ))}
        </fieldset>
        <fieldset className="flex flex-col gap-2">
            <legend className="mb-2 font-bold">Status</legend>
            {BILLING_HISTORY_STATUS_OPTIONS.map((option) => (
                <label
                    key={option.slug}
                    className="flex cursor-pointer items-center gap-3"
                >
                    <input
                        type="checkbox"
                        className="checkbox checkbox-primary"
                        checked={draft.statuses.includes(option.status)}
                        onChange={() =>
                            setDraft((prev) => ({
                                ...prev,
                                statuses: toggled(prev.statuses, option.status),
                            }))
                        }
                    />
                    {option.label}
                </label>
            ))}
        </fieldset>
    </div>
);
